import { getFirstString, getPathValue } from './report-field.utils';
import { parseReportJson } from './report-json.utils';

export interface FinancialPostingSource {
   rows: unknown[];
   isOracle: boolean;
}

const DEFAULT_MAPPED_SOURCE_PATHS = ['Rows', 'rows', 'Postings'] as const;

const DEFAULT_ORACLE_SOURCE_PATHS = [
   'financialPostings',
   'journalPostings.postings',
   'JournalPostings.Postings'
] as const;

const DEFAULT_RECEIPT_PATHS = [
   'ReceiptNumber',
   'receiptNumber',
   'ReceiptNo',
   'receiptNo',
   'TransactionNo',
   'transactionNo',
   'CheckNumber',
   'checkNumber'
] as const;

const DEFAULT_USER_PATHS = [
   'User',
   'user',
   'CashId',
   'cashId',
   'PostedByCashier',
   'postedByCashier',
   'CashierId',
   'cashierId',
   'cashierInfo.cashierId'
] as const;

const DEFAULT_USER_NAME_PATHS = [
   'UserName',
   'userName',
   'cashierInfo.userName'
] as const;

export function getFinancialPostingSource(
   reportJson: unknown,
   reportSourcePaths: readonly string[]
): FinancialPostingSource {
   const parsedReportJson = parseReportJson(reportJson);

   for (const path of reportSourcePaths) {
      const rows = getPathValue(parsedReportJson, path);

      if (Array.isArray(rows)) {
         return { rows, isOracle: false };
      }
   }

   for (const path of DEFAULT_MAPPED_SOURCE_PATHS) {
      const rows = getPathValue(parsedReportJson, path);

      if (Array.isArray(rows)) {
         return { rows, isOracle: false };
      }
   }

   for (const path of DEFAULT_ORACLE_SOURCE_PATHS) {
      const rows = getPathValue(parsedReportJson, path);

      if (Array.isArray(rows)) {
         return { rows, isOracle: true };
      }
   }

   return { rows: [], isOracle: false };
}

export function getFinancialPostingDate(row: unknown): string {
   return getFirstString(row, [
      'Date',
      'date',
      'TransactionDate',
      'transactionDate',
      'PostingDate',
      'postingDate',
      'BusinessDate',
      'businessDate'
   ]);
}

export function getFinancialPostingTime(row: unknown): string {
   return getFirstString(row, [
      'Time',
      'time',
      'TransactionTime',
      'transactionTime',
      'lastModifyDateTime',
      'LastModifyDateTime'
   ]);
}

export function getFinancialPostingRoom(row: unknown): string {
   return getFirstString(row, [
      'RoomNumber',
      'roomNumber',
      'RoomNo',
      'roomNo',
      'Room',
      'room',
      'guestInfo.roomId',
      'guestInfo.roomNumber'
   ]);
}

export function getFinancialPostingGuestName(row: unknown): string {
   return getFirstString(row, [
      'GuestName',
      'guestName',
      'Name',
      'name',
      'guestInfo.guestName'
   ]);
}

export function getFinancialPostingTransactionCode(row: unknown): string {
   return getFirstString(row, [
      'TransactionCode',
      'transactionCode',
      'TrnCode',
      'trnCode'
   ]);
}

export function getFinancialPostingReason(
   row: unknown,
   pathGroups: ReadonlyArray<readonly string[]>
): string {
   const values = pathGroups
      .map((paths) => getFirstString(row, paths))
      .filter((value) => value.length > 0);

   return Array.from(new Set(values)).join(' ');
}

export function getFinancialPostingReceipt(
   row: unknown,
   paths: readonly string[] = DEFAULT_RECEIPT_PATHS
): string {
   const receipt = getFirstString(row, paths);

   if (!receipt) {
      return '';
   }

   return receipt.startsWith('#') ? receipt : `#${receipt}`;
}

export function getFinancialPostingUser(
   row: unknown,
   primaryPaths: readonly string[] = DEFAULT_USER_PATHS,
   fallbackPaths: readonly string[] = DEFAULT_USER_NAME_PATHS
): string {
   const directUser = getFirstString(row, primaryPaths);

   if (directUser) {
      return directUser;
   }

   return getFirstString(row, fallbackPaths);
}
